import { makeObservable } from "mobx";

import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { operators } from "@/models/nodes/operators";

import type { DocumentModel } from "@/models/DocumentModel";
import type { ClassOf } from "@/models/nodes/BaseNodeModel";
import type { NodeId, NodeModel } from "@/models/nodes/types";
import type { NodeVisitor, VisitableNode } from "@/models/nodes/visitors/NodeVisitor";
import type {
  Operator,
  OperatorNodeProps,
  OperatorNodeConstructorProps,
  OperatorTypeValue,
} from "@corca/data";

export class OperatorNodeModel<OT extends OperatorTypeValue = OperatorTypeValue>
  extends BaseNodeModel<OperatorNodeProps<OT>>
  implements VisitableNode
{
  static type = "operator" as const;

  type = OperatorNodeModel.type;

  renderView: "root" | "functional" = "root";

  protected fillRequiredProps(props: OperatorNodeConstructorProps<OT>): OperatorNodeProps<OT> {
    this.reportMissingProps(props, ["operatorType"]);

    return props as OperatorNodeProps<OT>;
  }

  constructor(
    ...args: ConstructorParameters<typeof BaseNodeModel<OperatorNodeConstructorProps<OT>>>
  ) {
    super(...args);
    makeObservable(this, {
      parameters: true,
    });
  }

  static validateJson<T extends OperatorTypeValue = OperatorTypeValue>(
    props: unknown,
  ): props is OperatorNodeProps<T> {
    return !!(
      props &&
      typeof props === "object" &&
      "operatorType" in props &&
      typeof props.operatorType === "string" &&
      props.operatorType in operators
    );
  }

  static getRequiredChildrenCount(operatorType: OperatorTypeValue) {
    const { arity } = operators[operatorType];
    return arity === "binary" ? 2 : 1;
  }

  static create<T extends OperatorTypeValue>(
    doc: DocumentModel,
    operatorType: T,
    nodes: NodeModel[] = [],
    id?: NodeId,
    name?: string,
  ): OperatorNodeModel<T> {
    const { key } = operators[operatorType];

    const requiredCount = this.getRequiredChildrenCount(operatorType);
    for (let i = nodes.length; i < requiredCount; i++) {
      nodes.push(doc.create(InlineNodeModel));
    }

    return new OperatorNodeModel<T>(doc, id, { operatorType, name, key }, nodes);
  }

  static getOperatorSymbol(operatorType: OperatorTypeValue): string {
    return operators[operatorType].symbol;
  }

  static getOperatorBySymbol(str?: string): Operator | undefined {
    if (!str) {
      return undefined;
    }
    const operatorType = (Object.keys(operators) as Array<keyof typeof operators>).find(
      (ot) => operators[ot].symbol === str,
    );
    return operatorType && operators[operatorType];
  }

  get parameters() {
    return this.children.filter((n) => n.is(InlineNodeModel));
  }

  get operatorType(): OT {
    return this.props.operatorType;
  }

  get arity(): Operator["arity"] {
    return operators[this.props.operatorType].arity;
  }

  get isEditable(): boolean {
    return !!operators[this.props.operatorType].isEditable;
  }

  get operatorSymbol(): string {
    return this.props.name || OperatorNodeModel.getOperatorSymbol(this.props.operatorType);
  }

  get placement(): Operator["placement"] {
    return operators[this.props.operatorType].placement;
  }

  get isNested(): boolean {
    return !!operators[this.props.operatorType].isNested;
  }

  get paramsAreEmpty(): boolean {
    return this.parameters.every((param) => param.isEmpty);
  }

  is<C extends NodeModel>(type: ClassOf<C>): this is C;

  is<T extends OperatorTypeValue>(type: T): this is OperatorNodeModel<T>;

  is<C extends NodeModel>(type: OperatorTypeValue | ClassOf<C>): boolean {
    if (typeof type === "string") {
      return this.operatorType === type;
    }
    return this.type === type.type;
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitOperatorNode(this);
  }
}
